import { useEffect, useState } from "react";
import api from "../../lib/api";
import { KPI, Spinner, StatusBadge } from "../../components/Brand";
import { currency, fmtDate } from "../../lib/status";

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        (async () => {
            const [d1, d2] = await Promise.all([
                api.get("/reports/dashboard"),
                api.get("/bookings", { params: { limit: 8 } }),
            ]);
            setData(d1.data);
            setRecent(d2.data);
        })();
    }, []);

    if (!data) return <Spinner />;

    const k = data.kpi;
    const maxDay = Math.max(...data.last_7_days.map((d) => d.count), 1);

    return (
        <div className="space-y-10">
            <div>
                <div className="lx-label text-[#D4AF37] mb-2">Command Center</div>
                <h1 className="font-serif text-4xl text-white">
                    Today's <span className="italic">operations</span>.
                </h1>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI
                    label="Today Bookings"
                    value={k.today_bookings}
                    sub={`${k.total_bookings} all-time`}
                    testid="kpi-today"
                />
                <KPI
                    label="Pending Jobs"
                    value={k.pending_bookings}
                    sub="Active queue"
                    testid="kpi-pending"
                />
                <KPI
                    label="Month Revenue"
                    value={currency(k.revenue_month, "AED")}
                    sub={`${currency(k.platform_commission, "AED")} commission`}
                    testid="kpi-rev"
                />
                <KPI
                    label="Technicians"
                    value={`${k.technicians_online}/${k.technicians}`}
                    sub="Online / total"
                    testid="kpi-tech"
                />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                {/* Last 7 days chart */}
                <div className="lx-card-dark p-6 lg:col-span-2">
                    <div className="lx-label text-[#A09BA5] mb-6">Bookings — Last 7 days</div>
                    <div className="flex items-end gap-3 h-48">
                        {data.last_7_days.map((d) => (
                            <div key={d.date} className="flex-1 flex flex-col items-center">
                                <div
                                    className="w-full bg-gradient-to-t from-[#D4AF37] to-[#E8D399] transition-all"
                                    style={{
                                        height: `${(d.count / maxDay) * 100}%`,
                                        minHeight: "4px",
                                    }}
                                />
                                <div className="lx-label text-white/60 mt-2">{d.date}</div>
                                <div className="text-xs text-white mt-0.5">{d.count}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* By status */}
                <div className="lx-card-dark p-6">
                    <div className="lx-label text-[#A09BA5] mb-4">Status mix</div>
                    <div className="space-y-2">
                        {Object.entries(data.by_status).map(([s, c]) => (
                            <div key={s} className="flex items-center justify-between">
                                <StatusBadge status={s} />
                                <span className="text-sm text-white">{c}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <div className="lx-card-dark p-6">
                    <div className="lx-label text-[#A09BA5] mb-4">Top categories</div>
                    {data.top_categories.length === 0 && (
                        <div className="text-xs text-white/60">No data yet.</div>
                    )}
                    {data.top_categories.map((c, i) => (
                        <div
                            key={i}
                            className="py-3 border-b border-[#2B1438] last:border-0 flex justify-between items-center"
                        >
                            <div>
                                <div className="text-sm text-white">{c.name}</div>
                                <div className="text-xs text-white/60">{c.count} bookings</div>
                            </div>
                            <div className="font-serif text-lg text-[#D4AF37]">
                                {currency(c.revenue, "AED")}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="lx-card-dark p-6 lg:col-span-2">
                    <div className="lx-label text-[#A09BA5] mb-4">Recent bookings</div>
                    <div className="space-y-2">
                        {recent.slice(0, 6).map((b) => (
                            <div
                                key={b.id}
                                className="flex items-center justify-between gap-3 py-2 border-b border-[#2B1438] last:border-0"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm text-white truncate">
                                        {b.booking_number} · {b.service?.name}
                                    </div>
                                    <div className="text-xs text-white/60">
                                        {b.customer?.name} · {fmtDate(b.scheduled_at)}
                                    </div>
                                </div>
                                <StatusBadge status={b.status} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI label="Completed" value={k.completed_bookings} testid="kpi-comp" />
                <KPI label="Total Revenue" value={currency(k.revenue_total, "AED")} testid="kpi-rt" />
                <KPI label="Customers" value={k.customers} testid="kpi-cust" />
                <KPI label="Open Tickets" value={k.open_tickets} testid="kpi-tic" />
            </div>
        </div>
    );
}
